import { truncate } from './ai';
import type { ConfirmationStatus, REPLY_CLASS_VALUES } from './constants';
import { zonedNow } from './live';
import { tidyBlankLines } from './mail';
import { messageReply } from './quote';

/*
 * Whose turn a conversation is in (SPEC §1.1H) — the one spelling of "brez
 * odgovora" and "čaka naš odgovor", for the roster, Pregled and the digest.
 *
 * Every message gets a verdict: it `asks` (the other side owes an answer),
 * `closes` (nobody owes anything), or is `auto` (an out-of-office reply or a
 * bounce, which is nobody's word). The last word that is not `auto` decides:
 * ours asking means we wait for them, theirs asking means they wait for us.
 */

/** What the model or a coordinator may say about a message. */
export type ReplyClass = (typeof REPLY_CLASS_VALUES)[number];

/**
 * The mails that ask the person to confirm their schedule. Each waits on that
 * person's own answer, so a word on one household member's copy is theirs alone.
 */
export const WAITS_FOR_CONFIRMATION: readonly string[] = ['napotnica', 'sprememba'];

export interface ReplyVerdict {
  cls: ReplyClass | 'auto';
  /** Which rule decided — the label in the thread says it, and the replay counts it. */
  by: 'coordinator' | 'auto' | 'template' | 'ai' | 'rule' | 'text' | 'default';
  /** Only the text rules may be overturned by the model; everything above them is known. */
  aiEligible: boolean;
}

export interface TurnMessage {
  id: string;
  /** Notes are passed or not; they are never a word in the conversation. */
  direction: 'in' | 'out' | 'note';
  source: 'imap' | 'manual' | 'app';
  at: Date | string;
  /**
   * When the app wrote the row. A mail read in late carries the server's `at`
   * from hours before; an answer is only an answer to what we already held.
   */
  recordedAt?: Date | string;
  templateKey: string;
  subject: string;
  fromAddress: string;
  /** `null` where the body was not read — older rows the verdict only needs the direction of. */
  body: string | null;
  /** An out-of-office reply or a bounce, as the ingest found it (`inbox/auto.ts`). */
  auto?: boolean;
  aiClass?: ReplyClass | null;
  override?: ReplyClass | null;
}

/** A confirmation or refusal recorded after a message — an answer that is not a mail. */
export interface TurnAnswer {
  at: Date | string;
  by: 'volunteer' | 'coordinator';
}

export interface TurnInput {
  /** The person's conversation — an obvestilo is not part of it (`inConversation`). */
  messages: readonly TurnMessage[];
  answers: readonly TurnAnswer[];
  confirmationStatus: ConfirmationStatus;
  scheduleVersion: number;
  /** The newest briefing sent by mail: what a napotnica or sprememba is waiting on. */
  lastMailBriefing: { version: number; sentAt: Date | string } | null;
  /** The version their page shows — the newest briefing sent, by mail or silently. */
  publishedVersion: number | null;
  /** False for somebody not coming, or a team: nobody chases them for an answer. */
  chased: boolean;
  /** Problem reports from their page that were resolved on Live. */
  resolvedProblemMessageIds?: ReadonlySet<string>;
}

export interface CommsTurn {
  whose: 'them' | 'us';
  /** When the wait began: our asking mail, or the first of their unanswered ones. */
  since: string;
  /** Whole days since then, for the label. */
  days: number;
  /** `whose === 'them'` and the silence has passed the threshold. */
  noReply: boolean;
  /** The message that decided it. */
  messageId: string;
}

/** Caps what a text rule reads and what the model is sent. */
export const OWN_WORDS_MAX = 1500;

const time = (at: Date | string) => (typeof at === 'string' ? new Date(at) : at).getTime();

/**
 * What the sender wrote now: the quoted history cut off where `splitQuoted`
 * finds it — asked of mail read off the server alone, as it always is — and
 * the blank lines of their client tidied away.
 */
export function ownWords(m: Pick<TurnMessage, 'body' | 'source' | 'direction'>, max = OWN_WORDS_MAX): string {
  if (m.body === null) return '';
  return truncate(tidyBlankLines(messageReply({ ...m, body: m.body }).reply).trim(), max);
}

/**
 * Letters on either side end a word; `\b` knows only ASCII, and Slovenian ends
 * in č. The side before is matched rather than looked behind at: this module
 * loads on the volunteer's briefing page too, and Safari before 16.4 refuses a
 * lookbehind outright — the page would not load at all.
 */
const word = (alternatives: string) => new RegExp(`(?:^|[^\\p{L}])(${alternatives})(?!\\p{L})`, 'iu');

const REQUEST = word(
  [
    'sporoči(te)?',
    // "Potrdi prijavo v aplikaciji" tells them what to do on the day; it asks us nothing.
    'potrdi(te)?(?! prijavo)',
    // "Javi nam" asks for an answer; "javi se vodji" tells them whom to report to.
    'javi(te)? (nam|mi|nazaj)',
    'odgovori(te)?',
    'prosim(o)? za (odgovor|potrditev)',
    'ali (lahko|boš|bi|mi|nam|si|ste|greš|prideš)',
    'a (lahko|boš|bi|si|greš|prideš)',
    'bi (lahko|prosil|prosila)',
    'let (me|us) know',
    'please (confirm|reply|respond|let)',
    'confirm',
    'could you',
    'can you',
    'would you',
    'will you',
  ].join('|'),
);
/**
 * "If anything comes up, let us know" offers help; it does not wait for an
 * answer. Only a condition *before* the request makes it an offer — "sporoči,
 * če ti ustreza" still asks.
 */
const CONDITIONAL = word('če|ce|v primeru|kadarkoli|if|in case');
/**
 * An offer of help, with the verb that makes it one: "javi se, če karkoli
 * potrebuješ". It is taken out of the sentence before the sentence is read, so
 * a request beside it — "potrdi udeležbo in javi, če potrebuješ prevoz" — still
 * asks.
 */
const OFFER = new RegExp(
  '(?:^|[^\\p{L}])(?:(?:javi(?:te)?(?: se)?|sporoči(?:te)?|pokliči(?:te)?|piši(?:te)?|let (?:me|us) know),?\\s*)?' +
    '(?:če (?:boš |bi |boste )?(?:kaj|karkoli|kakšn\\p{L}*|potrebuješ|potrebujete|potrebovala|potreboval|rabiš|rabite|imaš vprašanj\\p{L}*|imate vprašanj\\p{L}*|bo kaj)' +
    '|if (?:you have any|you need|anything))[^,;]*' +
    // …and the verb after it, when the offer comes first: "če kaj ne bo jasno, nam sporoči".
    '(?:,\\s*(?:(?:nam|mi) )?(?:javi(?:te)?(?: se)?|sporoči(?:te)?|pokliči(?:te)?|piši(?:te)?|let (?:me|us) know)(?![\\p{L}]))?',
  'giu',
);
/**
 * A thank-you and nothing more: thanks, an okay, a see-you, and the few words
 * that go with them. Anything else written beside them is read as something
 * that may want an answer.
 */
const ACK_WORD =
  '(?:(?:najlepša |lepa )?hvala(?: lepa| vam| ti)?|ok|okej|okay|v redu|super|odlično|velja|se vidimo(?: v (?:petek|soboto|nedeljo)| tam| kmalu| jutri)?|za (?:info|informacije|obvestilo|vse|sporočilo)|to je vse|bom tam|pridem|lep dan|lep pozdrav|thanks|thank you|great|perfect|see you(?: there| soon)?|for the info|sounds good|[👍🙏🙂😊])';
const ACK_PART = new RegExp(`^${ACK_WORD}(?:\\s*${ACK_WORD})*$`, 'iu');
const ACK_MAX = 200;
/** "Živjo Nina," before the thanks — a greeting and a name, up to the first comma or line. */
const GREETING = /^(živjo|zdravo|pozdravljen\p{L}*|hej|hi|hello|dober dan|dear)(?!\p{L})[^,!.\n]{0,30}[,!.\n]\s*/iu;
/** "LP, Ana" after it — a sign-off and whatever shares its line. */
const SIGN_OFF = /\n\s*(?:lp|lep pozdrav|lepo pozdravljeni|pozdrav|best regards|regards|best|cheers)(?!\p{L})[^\n]{0,40}$/iu;
/**
 * A name on its own line below the sign-off: capitalised words and nothing
 * else, so a line that asks something after the sign-off stays part of the mail.
 */
const NAME_LINE = /\n\s*\p{Lu}\p{L}*(?: \p{Lu}\p{L}*){0,2}\s*$/u;

function withoutSignOff(text: string): string {
  if (SIGN_OFF.test(text)) return text.replace(SIGN_OFF, '');
  const named = NAME_LINE.exec(text);
  return named && SIGN_OFF.test(text.slice(0, named.index)) ? text.slice(0, named.index).replace(SIGN_OFF, '') : text;
}
/** What a client appends below the words: "Poslano iz mojega iPhona", or a "--" signature. */
const CLIENT_TAIL = /\n\s*(?:--\s*\n[^]*|poslano iz [^\n]*|sent from my [^\n]*)$/iu;

function asksSomething(text: string): boolean {
  const plain = text.replace(/https?:\/\/\S+/gi, '');
  if (plain.includes('?')) return true;
  return plain.split(/[.!\n]+/).some((sentence) => {
    const rest = sentence.replace(OFFER, ' ');
    const request = REQUEST.exec(rest);
    if (!request) return false;
    const condition = CONDITIONAL.exec(rest);
    return !condition || condition.index > request.index;
  });
}

/** Thanks and nothing more, past a greeting, a sign-off and what the mail client adds. */
function isAck(text: string): boolean {
  if (text.length > ACK_MAX) return false;
  const core = withoutSignOff(text.trim().replace(CLIENT_TAIL, '').trim()).replace(GREETING, '');
  const parts = core.split(/[,.!\n]+|\s+(?:in|and)\s+/iu).map((part) => part.trim()).filter((part) => part !== '');
  return parts.length > 0 && parts.every((part) => ACK_PART.test(part));
}

function verdictFor(m: TurnMessage, input: TurnInput): ReplyVerdict {
  if (m.override) return { cls: m.override, by: 'coordinator', aiEligible: false };
  if (m.auto) {
    return { cls: 'auto', by: 'auto', aiEligible: false };
  }
  if (WAITS_FOR_CONFIRMATION.includes(m.templateKey)) {
    const b = input.lastMailBriefing;
    // Waiting only on the briefing they were last mailed, while their page
    // shows the version they hold now: an older mail is superseded, and a
    // change nobody published to them is a publication to send, not an answer
    // to chase. A publication without a mail — a revert putting back what they
    // were mailed — leaves the mail still asking.
    const current =
      input.confirmationStatus === 'čaka' &&
      b !== null &&
      input.publishedVersion === input.scheduleVersion &&
      time(m.at) >= time(b.sentAt) - 60_000;
    return { cls: current ? 'asks' : 'closes', by: 'template', aiEligible: false };
  }
  if (m.templateKey !== '') return { cls: 'closes', by: 'template', aiEligible: false };
  // A problem resolved on Live has had its answer — a fact, which the model's
  // reading of the words does not overturn.
  if (m.direction === 'in' && input.resolvedProblemMessageIds?.has(m.id)) {
    return { cls: 'closes', by: 'rule', aiEligible: false };
  }
  if (m.aiClass) return { cls: m.aiClass, by: 'ai', aiEligible: false };
  const words = ownWords(m);
  // A call or mail the coordinator logged has been heard already, by the one
  // who logged it, and its words report what was said ("sporoči, da zamuja").
  // Only a question written into it still waits for an answer.
  if (m.direction === 'in' && m.source === 'manual') {
    return words.includes('?') ? { cls: 'asks', by: 'text', aiEligible: false } : { cls: 'closes', by: 'rule', aiEligible: false };
  }
  if (words !== '' && asksSomething(words)) return { cls: 'asks', by: 'text', aiEligible: true };
  if (words !== '' && isAck(words)) return { cls: 'closes', by: 'text', aiEligible: true };
  // An arriving mail is read until somebody says it needs nothing; our own
  // mail that asks nothing waits for nothing. With no words to read — none
  // written, or not loaded — there is nothing for the model either.
  return { cls: m.direction === 'in' ? 'asks' : 'closes', by: 'default', aiEligible: words !== '' };
}

const conversation = (messages: readonly TurnMessage[]) =>
  messages
    .filter((m) => m.direction !== 'note')
    .sort((a, b) => time(a.at) - time(b.at) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

/** The verdict on every message of the conversation, by id. */
export function replyVerdicts(input: TurnInput): Map<string, ReplyVerdict> {
  return new Map(conversation(input.messages).map((m) => [m.id, verdictFor(m, input)]));
}

/** The Ljubljana calendar date `days` after an ISO date. */
function addDays(isoDate: string, days: number): string {
  const d = new Date(`${isoDate}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * How long silence is patient: 24 h from a week before the event to its last
 * day, 72 h any other time. Dates are Ljubljana's.
 */
export function noReplyThresholdHours(
  now: Date,
  event: { startDate: string; endDate: string } | null,
): 24 | 72 {
  if (!event) return 72;
  const today = zonedNow(now).isoDate;
  return today >= addDays(event.startDate, -7) && today <= event.endDate ? 24 : 72;
}

/** The conversation's words in order, each with its verdict; automatic mail is no word. */
const wordsOf = (input: TurnInput) =>
  conversation(input.messages)
    .map((m) => ({ m, v: verdictFor(m, input) }))
    .filter(({ v }) => v.cls !== 'auto');

export interface DecidingWord {
  message: TurnMessage;
  verdict: ReplyVerdict;
  /** The word before it, for context. */
  previous: TurnMessage | null;
}

/**
 * The message a conversation hangs on: its newest word that is not automatic.
 * The classifier asks about this one, and the thread labels it.
 */
export function decidingWord(input: TurnInput): DecidingWord | null {
  const words = wordsOf(input);
  const last = words.at(-1);
  if (!last) return null;
  return { message: last.m, verdict: last.v, previous: words.at(-2)?.m ?? null };
}

export interface TurnReading {
  turn: CommsTurn | null;
  /**
   * The deciding word, and — when it asks but nobody waits — why not: an
   * answer given without a mail, or somebody nobody chases.
   */
  deciding: { messageId: string; settled: 'answered' | 'not-chased' | null } | null;
}

/** Whose turn it is, with the word that decides it — what a thread shows. */
export function readTurn(
  input: TurnInput & { now: Date; event: { startDate: string; endDate: string } | null },
): TurnReading {
  const words = wordsOf(input);
  const last = words.at(-1);
  if (!last) return { turn: null, deciding: null };
  const deciding = (settled: 'answered' | 'not-chased' | null) => ({ messageId: last.m.id, settled });
  if (last.v.cls === 'closes') return { turn: null, deciding: deciding(null) };
  // Their word is measured from when we held it, not when a server says it
  // arrived: an answer recorded before anyone could read it answers nothing.
  // Ours existed from the moment it was sent, however late the poll read it.
  const heldAt =
    last.m.direction === 'in' ? Math.max(time(last.m.at), time(last.m.recordedAt ?? last.m.at)) : time(last.m.at);
  const after = (by?: TurnAnswer['by']) =>
    input.answers.some((a) => time(a.at) > heldAt && (by === undefined || a.by === by));

  let since: Date | string;
  let whose: CommsTurn['whose'];
  if (last.m.direction === 'out') {
    if (after()) return { turn: null, deciding: deciding('answered') };
    if (!input.chased) return { turn: null, deciding: deciding('not-chased') };
    whose = 'them';
    since = last.m.at;
  } else {
    // Their answer on the page does not answer their own question; one we
    // recorded for them does.
    if (after('coordinator')) return { turn: null, deciding: deciding('answered') };
    whose = 'us';
    since = last.m.at;
    for (let i = words.length - 1; i >= 0 && words[i]!.m.direction === 'in'; i -= 1) {
      // A row whose text was not read asks only by default; the wait is not
      // stretched back over what nobody knows to be a question.
      if (words[i]!.m.body === null && words[i]!.v.by === 'default') break;
      if (words[i]!.v.cls === 'asks') since = words[i]!.m.at;
    }
  }
  const hours = (input.now.getTime() - time(since)) / 3_600_000;
  return {
    turn: {
      whose,
      since: new Date(time(since)).toISOString(),
      days: Math.max(0, Math.floor(hours / 24)),
      noReply: whose === 'them' && hours >= noReplyThresholdHours(input.now, input.event),
      messageId: last.m.id,
    },
    deciding: deciding(null),
  };
}

export function commsTurn(
  input: TurnInput & { now: Date; event: { startDate: string; endDate: string } | null },
): CommsTurn | null {
  return readTurn(input).turn;
}
